import { useEffect, useState } from 'react'

import PlusIcon from '@/assets/icons/plus.svg?react'
import ReduceIcon from '@/assets/icons/reduce.svg?react'
import { cn } from '@/utils/style'

type DataAttributes = {
  [key: `data-${string}`]: string | number | undefined
}

interface NumberInputProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>,
    DataAttributes {
  value: number
  onChange?: (value: number) => void
  className?: string
  max?: number
  min?: number
  step?: number
  disabled?: boolean
  inputProps?: Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'className' | 'min' | 'max' | 'step' | 'value' | 'onChange' | 'onBlur'
  >
}

export function NumberInput({
  value,
  className,
  min = 0,
  max = Infinity,
  step = 1,
  disabled = false,
  onChange,
  inputProps,
  ...rootProps
}: NumberInputProps) {
  const [inputValue, setInputValue] = useState(String(value))

  useEffect(() => {
    setInputValue(String(value))
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    if (/^\d*$/.test(newValue)) {
      setInputValue(newValue)
    }
  }

  const handleBlur = () => {
    const numericValue = Number(inputValue)

    if (numericValue > max) {
      onChange?.(max)
      setInputValue(`${max}`)
    } else if (numericValue < min) {
      onChange?.(min)
      setInputValue(`${min}`)
    } else {
      onChange?.(numericValue)
    }
  }

  return (
    <div
      {...rootProps}
      className={cn(
        'flex justify-center items-center p-0.5 bg-panel-gray-100 rounded-lg',
        className,
      )}
    >
      <div
        className={cn(
          'w-10 h-10 rounded-md shadow-card-100 bg-panel-white flex items-center justify-center cursor-pointer',
          { 'cursor-not-allowed': value <= min || disabled },
        )}
        onClick={() => {
          if (value > min && !disabled) {
            onChange?.(Math.max(value - step, min))
          }
        }}
        onMouseDown={(e) => {
          e.preventDefault()
        }}
      >
        <ReduceIcon
          className={value <= min || disabled ? 'text-tertiary' : 'text-primary'}
          width={20}
          height={20}
        />
      </div>
      <input
        {...inputProps}
        className={cn(
          'flex-1 min-w-0 flex items-center justify-center t-sb text-center h-10 mx-0.5 bg-transparent outline-none',
          disabled ? 'cursor-not-allowed text-tertiary' : 'text-primary',
        )}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <div
        className={cn(
          'w-10 h-10 rounded-md shadow-card-100 bg-panel-white flex items-center justify-center cursor-pointer',
          { 'cursor-not-allowed': value >= max || disabled },
        )}
        onMouseDown={(e) => {
          e.preventDefault()
        }}
        onClick={() => {
          if (value < max && !disabled) {
            onChange?.(Math.min(value + step, max))
          }
        }}
      >
        <PlusIcon
          className={value >= max || disabled ? 'text-tertiary' : 'text-primary'}
          width={20}
          height={20}
        />
      </div>
    </div>
  )
}
